import type { Ref } from "react";
import { Breadcrumbs, type Crumb } from "../ui/breadcrumbs";
import { Button } from "../ui/button";
import { Icon } from "../ui/icon";
import { DensityToggle } from "./DensityToggle";
import { NotificationBell } from "./NotificationBell";
import type { BellState } from "./shell-counts";

type TopBarProps = Readonly<{
  crumbs: readonly Crumb[];
  /** Opens the navigation drawer (a narrow screen only). */
  onOpenMenu: () => void;
  menuOpen: boolean;
  /** The drawer returns focus to this button when it closes. */
  menuButtonRef: Ref<HTMLButtonElement>;
  /** Opens the search dialog. The shell also opens it on ⌘K and Ctrl+K. */
  onOpenSearch: () => void;
  /** The search dialog is open (the button says so to a screen reader). */
  searchOpen: boolean;
  bell: BellState;
}>;

/** The key of the shortcut as this machine names it: ⌘ on a Mac, Ctrl elsewhere. */
function shortcutLabel(): string {
  const apple = typeof navigator !== "undefined" && /Mac|iPhone|iPad/u.test(navigator.platform);
  return apple ? "⌘K" : "Ctrl K";
}

/**
 * The bar above every page: the menu button on a narrow screen, the trail of the route, the search field (a button that opens the dialog),
 * the density choice and the bell. On a narrow screen the search is an icon and the trail shortens by truncation, so the bar never scrolls
 * sideways.
 */
export function TopBar({
  crumbs,
  onOpenMenu,
  menuOpen,
  menuButtonRef,
  onOpenSearch,
  searchOpen,
  bell,
}: TopBarProps) {
  return (
    <header className="sticky top-0 z-[var(--lf-z-sticky)] flex h-15 shrink-0 items-center gap-2 rounded-t-[19px] border-b border-hairline bg-background px-3 sm:px-4 md:pr-5 md:pl-7">
      <Button
        ref={menuButtonRef}
        variant="ghost"
        size="icon"
        className="md:hidden"
        aria-label="Open navigation menu"
        aria-haspopup="dialog"
        aria-expanded={menuOpen}
        onClick={onOpenMenu}
      >
        <Icon name="menu-01" size="lg" />
      </Button>
      <Breadcrumbs items={crumbs} className="flex-1" />
      <div className="flex shrink-0 items-center gap-1 compact:gap-2 md:gap-2">
        <Button
          variant="outline"
          size="icon"
          aria-haspopup="dialog"
          aria-expanded={searchOpen}
          aria-keyshortcuts="Control+K Meta+K"
          onClick={onOpenSearch}
          className="rounded-xl border-transparent bg-transparent md:w-80 md:compact:w-80 md:justify-start md:gap-2 md:border-input md:bg-card md:pr-2 md:pl-3 md:text-left md:font-normal md:text-muted-foreground"
        >
          <Icon name="search" size="md" className="text-foreground md:text-muted-foreground" />
          <span className="sr-only md:not-sr-only md:flex-1 md:text-[15px]">Search anything</span>
          <kbd className="hidden rounded-md bg-muted px-1.5 py-0.5 font-sans text-xs font-semibold text-muted-foreground md:inline">
            {shortcutLabel()}
          </kbd>
        </Button>
        <DensityToggle />
        <NotificationBell state={bell} />
      </div>
    </header>
  );
}
